import { createContext, useContext, useMemo } from 'react'
import { createApi, type Api } from '../lib/api'
import { createMockApi } from '../lib/mockApi'
import { createAsanaApi, type AsanaApi } from '../lib/asana'
import { useAccessToken } from './useAuth'

// Local visual-test mode: VITE_USE_MOCK_API=true swaps the real API for an
// in-memory mock. No backend, no Cosmos, data resets on refresh.
const USE_MOCK = import.meta.env.VITE_USE_MOCK_API === 'true'

interface ApiContextValue {
  api: Api
  asanaApi: AsanaApi
}

export const ApiContext = createContext<ApiContextValue | null>(null)

export function useApi(): Api {
  const ctx = useContext(ApiContext)
  if (!ctx) throw new Error('useApi must be used within ApiProvider')
  return ctx.api
}

export function useAsanaApi(): AsanaApi {
  const ctx = useContext(ApiContext)
  if (!ctx) throw new Error('useAsanaApi must be used within ApiProvider')
  return ctx.asanaApi
}

export function useApiContextValue(): ApiContextValue {
  const getToken = useAccessToken()
  return useMemo(() => ({
    api: USE_MOCK ? createMockApi() : createApi(getToken),
    asanaApi: createAsanaApi(getToken),
  }), [getToken])
}
